
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    languages: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3hhkbedn2fs4irjn320', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          languages: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyStats = [
    { number: t('stat1Number'), label: t('stat1Label') },
    { number: t('stat2Number'), label: t('stat2Label') },
    { number: t('stat3Number'), label: t('stat3Label') },
    { number: t('stat4Number'), label: t('stat4Label') }
  ];

  const faqItems = [
    {
      question: t('faq1Question'),
      answer: t('faq1Answer')
    },
    {
      question: t('faq2Question'),
      answer: t('faq2Answer')
    },
    {
      question: t('faq3Question'),
      answer: t('faq3Answer')
    },
    {
      question: t('faq4Question'),
      answer: t('faq4Answer')
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      {/* About Us Section */}
      <section id="about" className="py-20 px-6 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          {/* Company Introduction */}
          <div className="text-center mb-20">
            <h2 className="text-indigo-400 text-xl mb-3">{t('aboutSubtitle')}</h2>
            <h3 className="text-5xl font-bold mb-8 text-white">{t('aboutTitle')}</h3>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {t('aboutDescription')}
            </p>
          </div>

          {/* Company Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {companyStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-indigo-400 mb-2">{stat.number}</div>
                <div className="text-gray-300 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div className="bg-gray-900 rounded-2xl p-8">
              <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-rocket-line ri-2x text-indigo-400"></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">{t('ourMission')}</h4>
              <p className="text-gray-300 leading-relaxed">
                {t('missionText')}
              </p>
            </div>
            
            <div className="bg-gray-900 rounded-2xl p-8">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line ri-2x text-purple-400"></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">{t('ourVision')}</h4>
              <p className="text-gray-300 leading-relaxed">
                {t('visionText')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-indigo-400 text-xl mb-3">{t('faqSubtitle')}</h2>
            <h3 className="text-4xl font-bold mb-6 text-white">{t('faqTitle')}</h3>
            <p className="text-gray-300">{t('faqDescription')}</p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border border-gray-800 rounded-lg overflow-hidden">
                <button 
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none cursor-pointer hover:bg-gray-900/50 transition-colors"
                >
                  <span className="text-lg font-medium text-white">{item.question}</span>
                  <i className={`ri-${openFaqIndex === index ? 'subtract' : 'add'}-line ri-lg text-gray-400`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[400px] overflow-hidden mx-auto max-w-7xl rounded-3xl my-20">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300"></div>
        <div className="absolute left-[10%] top-1/2 -translate-y-1/2 w-[40%] flex items-center gap-4">
          <div className="w-2/3 relative z-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/98396f56-37d0-4c87-ace9-f7de22e11642.jpg" 
              alt={t('abstractShape')}
              className="w-full h-auto object-cover scale-150"
            />
          </div>
          <div className="w-1/2 relative z-10 -ml-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/3cf1d9a4-5823-403b-b9da-7dcf837b7aea.jpg" 
              alt={t('abstractShape2')}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
        <div className="absolute left-[55%] top-1/2 -translate-y-1/2 text-left">
          <h2 className="text-5xl font-bold text-gray-900 mb-8">
            {t('ctaTitle1')}<br />{t('ctaTitle2')}
          </h2>
          <Button 
            onClick={() => {
              const element = document.getElementById('contact-form');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-medium px-12 py-4 rounded-full hover:opacity-90 transition-opacity text-xl"
          >
            {t('contactUs')}
          </Button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 px-6 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-indigo-400 text-xl mb-3">{t('contactSubtitle')}</h2>
            <h3 className="text-4xl font-bold mb-6 text-white">{t('contactTitle')}</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('contactDescription')}
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-8 shadow-2xl">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('name')} {t('required')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm"
                    placeholder={t('namePlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('email')} {t('required')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm"
                    placeholder={t('emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('company')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm"
                    placeholder={t('companyPlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="languages" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('targetLanguages')}
                  </label>
                  <input
                    type="text"
                    id="languages"
                    name="languages"
                    value={formData.languages}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm"
                    placeholder={t('languagesPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('projectDetails')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={1000}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm resize-none"
                  placeholder={t('messagePlaceholder')}
                ></textarea>
                <div className="text-right text-xs text-gray-400 mt-1">
                  {formData.message.length}/1000 {t('characterCount')}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line ri-lg animate-spin mr-2"></i>
                      {t('submitting')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill ri-lg mr-2"></i>
                      {t('getQuote')}
                    </>
                  )}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 py-4 text-lg border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                  onClick={() => {
                    window.location.href = 'mailto:info@flidge-ai.com';
                  }}
                >
                  <i className="ri-mail-line ri-lg mr-2"></i>
                  {t('emailContact')}
                </Button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-green-400 text-center">
                  <i className="ri-check-circle-line ri-lg mr-2"></i>
                  {t('submitSuccess')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-center">
                  <i className="ri-error-warning-line ri-lg mr-2"></i>
                  {t('submitError')}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
